import {T, isSolid} from './art.ts';
import {isWet} from './passable.ts';

const soft = (t: number) => t === T.GROUND || t === T.MOSS || t === T.SAND || t === T.TUFT;

function clearingReach(tiles: number[][], spawn: number[], blocked = new Set<number>()) {
  const h = tiles.length, w = tiles[0].length;
  const first = spawn[1] * w + spawn[0], seen = new Set([first]), q = [first];
  for (let i = 0; i < q.length; i++) {
    const x = q[i] % w, y = Math.floor(q[i] / w);
    for (const [dx, dy] of [[1, 0], [0, 1], [-1, 0], [0, -1]]) {
      const a = x + dx, b = y + dy, k = b * w + a;
      if (a < 0 || b < 0 || a >= w || b >= h || seen.has(k) || blocked.has(k)) continue;
      if (isSolid(tiles[b][a]) || isWet(tiles[b][a])) continue;
      seen.add(k); q.push(k);
    }
  }
  return seen;
}

/** A nearby workshop may occupy open ground, but cannot sever the clearing. */
export function openingWorkshop(tiles: number[][], spawn: number[]): number[] | null {
  const h = tiles.length, w = tiles[0].length;
  const flood = (blocked = new Set<number>()) => clearingReach(tiles, spawn, blocked);
  const before = flood(), candidates: {x: number; y: number; score: number}[] = [];
  for (let y = 2; y < h - 2; y++) for (let x = 2; x < w - 3; x++) {
    const d = Math.hypot(x + 0.5 - spawn[0], y - spawn[1]);
    if (d < 2.5 || d > 5) continue;
    if (!soft(tiles[y][x]) || !soft(tiles[y][x + 1]) || !before.has(y * w + x) || !before.has(y * w + x + 1)) continue;
    const access = [[x + 2, y], [x + 1, y - 1], [x + 1, y + 1]]
      .filter(([a, b]) => soft(tiles[b][a]) && before.has(b * w + a));
    if (access.length >= 2) candidates.push({x, y, score: Math.abs(d - 3.5) + (y < spawn[1] ? 1 : 0)});
  }
  candidates.sort((a, b) => a.score - b.score || a.y - b.y || a.x - b.x);
  for (const {x, y} of candidates) {
    const occupied = new Set([y * w + x, y * w + x + 1]);
    const after = flood(occupied);
    if ([...before].every(k => occupied.has(k) || after.has(k))) return [x, y];
  }
  return null;
}

/** One cuttable source in open view; never replace scenery or close a route. */
export function openingResource(tiles: number[][], spawn: number[], workshop: number[]): number[] | null {
  const h = tiles.length, w = tiles[0].length, [fx, fy] = workshop;
  const blocked = new Set([fy * w + fx, fy * w + fx + 1]);
  const before = clearingReach(tiles, spawn, blocked);
  const candidates: {x: number; y: number; score: number}[] = [];
  for (let y = 2; y < h - 2; y++) for (let x = 2; x < w - 2; x++) {
    const k = y * w + x, d = Math.hypot(x - fx - 1, y - fy);
    if (!before.has(k) || blocked.has(k) || !soft(tiles[y][x]) || d < 2 || d > 4) continue;
    if (Math.hypot(x - spawn[0], y - spawn[1]) < 2 || Math.hypot(x - fx, y - fy) < 2.5) continue;
    let obscured = false;
    for (let dy = -1; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
      const t = tiles[y + dy]?.[x + dx];
      if (t === T.TREE || (dy >= 0 && Math.abs(dx) <= 1 && (t === T.CLIFF || t === T.ROCK || t === T.STONE))) obscured = true;
    }
    if (obscured) continue;
    const access = [[x-1,y],[x+1,y],[x,y-1],[x,y+1]];
    if (access.filter(([a,b]) => before.has(b*w+a) && soft(tiles[b][a])).length < 2) continue;
    candidates.push({x,y,score:Math.abs(d-2.5)+Math.hypot(x-spawn[0],y-spawn[1])*0.15});
  }
  candidates.sort((a,b) => a.score-b.score || a.y-b.y || a.x-b.x);
  for (const {x,y} of candidates) {
    const k = y*w+x, occupied = new Set([...blocked,k]);
    const after = clearingReach(tiles,spawn,occupied);
    if ([...before].every(n => n === k || after.has(n))) return [x,y];
  }
  return null;
}
